import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../lib/utils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Building,
  Users,
  UserPlus,
  Stethoscope,
  Heart,
  Loader2,
  LogOut,
  Phone,
  Mail,
  MapPin,
  Trash2,
  Power,
  Star,
  Calendar,
  Save,
  Eye,
  EyeOff,
  Activity,
  CheckCircle,
  Clock,
  IndianRupee,
  Edit,
  X,
  Home,
  User,
  Upload,
  Image as ImageIcon,
  IdCard,
} from 'lucide-react';

const PHYSIGN_LOGO = "https://customer-assets.emergentagent.com/job_4bf5f59f-ff2a-4b89-ab5f-09ffb3b85c36/artifacts/91dqnx5f_high-resolution-color-logo-3.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper: convert relative /api/uploads URL to absolute
const toAbsoluteUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

// File upload component with preview
function ImageUploadField({ label, value, onChange, imageType, helperText, aspect = 'square', testId }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5 MB allowed.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/clinic/upload-image?image_type=${imageType}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      onChange(data.file_url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const previewUrl = toAbsoluteUrl(value);
  const isPdf = value && value.toLowerCase().endsWith('.pdf');

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <Label className="text-xs">{label}</Label>
      <div
        className={`relative border-2 border-dashed rounded-xl overflow-hidden transition-all ${value ? 'border-purple-300 bg-purple-50/40' : 'border-slate-200 bg-slate-50 hover:border-slate-300'} ${aspect === 'banner' ? 'aspect-[3/1]' : 'aspect-square'}`}
      >
        {value && !isPdf ? (
          <img src={previewUrl} alt={label} className="w-full h-full object-cover" />
        ) : value && isPdf ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-3">
            <IdCard className="w-8 h-8 text-purple-500" />
            <span className="text-xs text-slate-600 truncate max-w-[80%]">PDF uploaded</span>
            <a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs text-purple-600 underline">View</a>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-slate-400 hover:text-purple-500 transition-colors"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Upload className="w-5 h-5" />
                <span className="text-[11px] font-medium">Upload</span>
              </>
            )}
          </button>
        )}
        {value && (
          <div className="absolute top-1.5 right-1.5 flex gap-1">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="bg-white/90 hover:bg-white text-slate-700 rounded-md px-2 py-0.5 text-[10px] font-medium shadow-sm"
              data-testid={`${testId}-replace`}
            >
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Replace'}
            </button>
            <button
              type="button"
              onClick={() => onChange('')}
              className="bg-white/90 hover:bg-white text-red-600 rounded-md px-2 py-0.5 text-[10px] font-medium shadow-sm"
              data-testid={`${testId}-remove`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={handleUpload}
      />
      {helperText && <p className="text-[10px] text-slate-400">{helperText}</p>}
    </div>
  );
}

export default function ClinicDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [clinic, setClinic] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  // Add member form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [memberForm, setMemberForm] = useState({
    name: '', phone: '', email: '', password: '', role: 'doctor',
    specialization: '', qualification: '', experience_years: '',
    consultation_fee: '', home_visit_fee: '', services: [],
    govt_id: '', license_number: '', bio: '',
    available_days: [], clinic_address: '',
    govt_id_photo: '', profile_photo: '', banner_photo: '',
  });

  // Clinic profile form
  const [clinicForm, setClinicForm] = useState({
    clinic_name: '', clinic_address: '', location: '', bio: '', services: [],
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Appointments
  const [appointments, setAppointments] = useState([]);
  const [loadingApts, setLoadingApts] = useState(false);

  // Edit member
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiRequest('/clinic/members');
      setMembers(data.members || []);
      setClinic(data.clinic || {});
      setClinicForm({
        clinic_name: data.clinic?.clinic_name || '',
        clinic_address: data.clinic?.clinic_address || '',
        location: data.clinic?.location || '',
        bio: data.clinic?.description || '',
        services: data.clinic?.services || [],
      });
    } catch (error) {
      toast.error('Failed to load clinic data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
    fetchAppointments();
  }, [fetchMembers]);

  const fetchAppointments = async () => {
    setLoadingApts(true);
    try {
      const data = await apiRequest('/clinic/appointments');
      setAppointments(data.appointments || []);
    } catch (error) {
      console.error('Failed to load appointments');
    } finally {
      setLoadingApts(false);
    }
  };

  const handleUpdateAppointmentStatus = async (aptId, status) => {
    try {
      await apiRequest(`/appointments/${aptId}/status?status=${status}`, { method: 'PUT' });
      toast.success(`Appointment ${status}`);
      fetchAppointments();
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    }
  };

  const handleStartEdit = (member) => {
    let bioData = {};
    try { bioData = JSON.parse(member.profile?.bio || '{}'); } catch { bioData = {}; }
    setEditingMember(member.id);
    setEditForm({
      name: member.name || '',
      email: member.email || '',
      specialization: member.profile?.specialization || '',
      qualification: member.profile?.qualification || '',
      experience_years: member.profile?.experience_years || '',
      consultation_fee: member.profile?.consultation_fee || '',
      home_visit_fee: member.profile?.home_visit_fee || '',
      govt_id: bioData.govt_id || '',
      license_number: bioData.license_number || '',
      bio: bioData.text || '',
      govt_id_photo: bioData.govt_id_photo || '',
      profile_photo: bioData.profile_photo || member.avatar || '',
      banner_photo: bioData.banner_photo || '',
      available_days: member.profile?.available_days || [],
      clinic_address: member.profile?.clinic_address || '',
      services: member.profile?.services || [],
    });
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        experience_years: editForm.experience_years ? parseInt(editForm.experience_years) : null,
        consultation_fee: editForm.consultation_fee ? parseFloat(editForm.consultation_fee) : null,
        home_visit_fee: editForm.home_visit_fee ? parseFloat(editForm.home_visit_fee) : null,
      };
      await apiRequest(`/clinic/edit-member/${editingMember}`, { method: 'PUT', body: JSON.stringify(payload) });
      toast.success('Member updated successfully!');
      setEditingMember(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.message || 'Failed to update member');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setAddingMember(true);
    try {
      const payload = {
        ...memberForm,
        experience_years: memberForm.experience_years ? parseInt(memberForm.experience_years) : null,
        consultation_fee: memberForm.consultation_fee ? parseFloat(memberForm.consultation_fee) : null,
        home_visit_fee: memberForm.home_visit_fee ? parseFloat(memberForm.home_visit_fee) : null,
      };
      await apiRequest('/clinic/add-member', { method: 'POST', body: JSON.stringify(payload) });
      toast.success(`${memberForm.role === 'doctor' ? 'Doctor' : 'Nurse'} added successfully!`);
      setShowAddForm(false);
      setMemberForm({ name: '', phone: '', email: '', password: '', role: 'doctor', specialization: '', qualification: '', experience_years: '', consultation_fee: '', home_visit_fee: '', services: [], govt_id: '', license_number: '', bio: '', available_days: [], clinic_address: '', govt_id_photo: '', profile_photo: '', banner_photo: '' });
      fetchMembers();
    } catch (error) {
      toast.error(error.message || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName}? This will delete their account and all data.`)) return;
    try {
      await apiRequest(`/clinic/remove-member/${memberId}`, { method: 'DELETE' });
      toast.success(`${memberName} removed from clinic`);
      fetchMembers();
    } catch (error) {
      toast.error(error.message || 'Failed to remove member');
    }
  };

  const handleToggleMemberAvailability = async (memberId) => {
    try {
      const data = await apiRequest(`/clinic/member/${memberId}/availability`, { method: 'PUT' });
      toast.success(data.message);
      fetchMembers();
    } catch (error) {
      toast.error('Failed to toggle availability');
    }
  };

  const handleSaveClinicProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await apiRequest('/clinic/profile', { method: 'PUT', body: JSON.stringify(clinicForm) });
      toast.success('Clinic profile updated!');
      fetchMembers();
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const activeDoctors = members.filter(m => m.role === 'doctor' && m.is_active);
  const activeNurses = members.filter(m => m.role === 'nurse' && m.is_active);
  const totalAppointments = members.reduce((sum, m) => sum + (m.appointment_count || 0), 0);
  const pendingApts = appointments.filter(a => a.status === 'pending');
  const confirmedApts = appointments.filter(a => a.status === 'confirmed');
  const completedApts = appointments.filter(a => a.status === 'completed');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="clinic-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-lg font-bold text-[#3B1C73]">Physign</span>
          </Link>
          <div className="flex items-center gap-3">
            <Badge className="bg-purple-100 text-purple-700 px-3 py-1">
              <Building className="w-3.5 h-3.5 mr-1" />Clinic
            </Badge>
            <span className="text-sm font-medium text-slate-700 hidden sm:inline">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">{clinic.clinic_name || 'My Clinic'}</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your clinic's doctors and nurses</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="tab-appointments">
              Appointments {pendingApts.length > 0 && <Badge className="ml-1 bg-amber-100 text-amber-700 text-[10px] px-1.5">{pendingApts.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="members" data-testid="tab-members">Members ({members.length})</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">Clinic Profile</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="clinic-stats">
              <Card className="border-l-4 border-l-purple-400">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Members</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="stat-total-members">{members.length}</p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-blue-400">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Active Doctors</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{activeDoctors.length}</p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Stethoscope className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-rose-400">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Active Nurses</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{activeNurses.length}</p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-rose-50 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-rose-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-amber-400">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Bookings</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{totalAppointments}</p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Member List */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-500" />
                  Your Team
                </CardTitle>
                <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={() => { setActiveTab('members'); setShowAddForm(true); }} data-testid="add-member-overview-btn">
                  <UserPlus className="w-4 h-4 mr-1" />Add Member
                </Button>
              </CardHeader>
              <CardContent>
                {members.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-500">No members yet. Add your first doctor or nurse!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {members.map(member => (
                      <MemberCard key={member.id} member={member} onToggle={handleToggleMemberAvailability} onRemove={handleRemoveMember} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <div className="space-y-4" data-testid="clinic-appointments">
              {/* Status Filter */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Badge className="bg-amber-100 text-amber-700">{pendingApts.length} Pending</Badge>
                  <Badge className="bg-blue-100 text-blue-700">{confirmedApts.length} Confirmed</Badge>
                  <Badge className="bg-emerald-100 text-emerald-700">{completedApts.length} Completed</Badge>
                </div>
              </div>

              {loadingApts ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>
              ) : appointments.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Calendar className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-500">No appointments yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {appointments.map(apt => (
                    <ClinicAppointmentCard key={apt.id} apt={apt} onAction={handleUpdateAppointmentStatus} />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Manage Members</h2>
              <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={() => setShowAddForm(!showAddForm)} data-testid="add-member-btn">
                <UserPlus className="w-4 h-4 mr-2" />{showAddForm ? 'Cancel' : 'Add Doctor / Nurse'}
              </Button>
            </div>

            {/* Add Member Form */}
            {showAddForm && (
              <Card className="mb-6 border-2 border-purple-200" data-testid="add-member-form">
                <CardHeader>
                  <CardTitle className="text-base">Add New Member</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAddMember} className="space-y-4">
                    {/* Role Selection */}
                    <div className="flex gap-3">
                      <button type="button" onClick={() => setMemberForm({...memberForm, role: 'doctor'})}
                        className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${memberForm.role === 'doctor' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid="member-role-doctor">
                        <Stethoscope className={`w-4 h-4 ${memberForm.role === 'doctor' ? 'text-blue-600' : 'text-slate-400'}`} />
                        <span className={`text-sm font-medium ${memberForm.role === 'doctor' ? 'text-blue-700' : 'text-slate-600'}`}>Doctor</span>
                      </button>
                      <button type="button" onClick={() => setMemberForm({...memberForm, role: 'nurse'})}
                        className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${memberForm.role === 'nurse' ? 'border-rose-500 bg-rose-50' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid="member-role-nurse">
                        <Heart className={`w-4 h-4 ${memberForm.role === 'nurse' ? 'text-rose-600' : 'text-slate-400'}`} />
                        <span className={`text-sm font-medium ${memberForm.role === 'nurse' ? 'text-rose-700' : 'text-slate-600'}`}>Nurse</span>
                      </button>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Full Name *</Label>
                        <Input value={memberForm.name} onChange={(e) => setMemberForm({...memberForm, name: e.target.value})} placeholder="Dr. Amit Kumar" required data-testid="member-name" />
                      </div>
                      <div>
                        <Label>Phone Number *</Label>
                        <Input value={memberForm.phone} onChange={(e) => setMemberForm({...memberForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})} placeholder="10-digit number" maxLength={10} required data-testid="member-phone" />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input type="email" value={memberForm.email} onChange={(e) => setMemberForm({...memberForm, email: e.target.value})} placeholder="doctor@email.com" data-testid="member-email" />
                      </div>
                      <div>
                        <Label>Login Password *</Label>
                        <div className="relative">
                          <Input type={showPassword ? 'text' : 'password'} value={memberForm.password} onChange={(e) => setMemberForm({...memberForm, password: e.target.value})} placeholder="Set a password" required data-testid="member-password" />
                          <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowPassword(!showPassword)}>
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Professional Details */}
                    <div className="border-t pt-4 mt-2">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">Professional Details</h4>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Specialization</Label>
                          <Input value={memberForm.specialization} onChange={(e) => setMemberForm({...memberForm, specialization: e.target.value})} placeholder={memberForm.role === 'doctor' ? 'e.g. Cardiology' : 'e.g. ICU Nursing'} data-testid="member-specialization" />
                        </div>
                        <div>
                          <Label>Qualification</Label>
                          <Input value={memberForm.qualification} onChange={(e) => setMemberForm({...memberForm, qualification: e.target.value})} placeholder={memberForm.role === 'doctor' ? 'e.g. MBBS, MD' : 'e.g. GNM, BSc Nursing'} data-testid="member-qualification" />
                        </div>
                        <div>
                          <Label>Experience (years)</Label>
                          <Input type="number" value={memberForm.experience_years} onChange={(e) => setMemberForm({...memberForm, experience_years: e.target.value})} placeholder="e.g. 5" data-testid="member-experience" />
                        </div>
                        <div>
                          <Label>{memberForm.role === 'doctor' ? 'Medical License No.' : 'Nursing Registration No.'}</Label>
                          <Input value={memberForm.license_number} onChange={(e) => setMemberForm({...memberForm, license_number: e.target.value})} placeholder={memberForm.role === 'doctor' ? 'e.g. MH/12345/2020' : 'e.g. NUR/12345'} data-testid="member-license" />
                        </div>
                        <div>
                          <Label>Govt ID (Aadhaar / PAN)</Label>
                          <Input value={memberForm.govt_id} onChange={(e) => setMemberForm({...memberForm, govt_id: e.target.value})} placeholder="e.g. 1234-5678-9012" data-testid="member-govt-id" />
                        </div>
                        <div>
                          <Label>Clinic/Visit Address</Label>
                          <Input value={memberForm.clinic_address} onChange={(e) => setMemberForm({...memberForm, clinic_address: e.target.value})} placeholder="Full address for visits" data-testid="member-clinic-address" />
                        </div>
                      </div>
                    </div>

                    {/* Photo Uploads */}
                    <div className="border-t pt-4 mt-2">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">Documents & Photos</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="add-member-photo-uploads">
                        <ImageUploadField
                          label="Profile Photo"
                          value={memberForm.profile_photo}
                          onChange={(url) => setMemberForm({ ...memberForm, profile_photo: url })}
                          imageType="profile_photo"
                          helperText="JPG/PNG, max 5MB"
                          testId="upload-profile-photo"
                        />
                        <ImageUploadField
                          label="Govt ID Photo"
                          value={memberForm.govt_id_photo}
                          onChange={(url) => setMemberForm({ ...memberForm, govt_id_photo: url })}
                          imageType="govt_id_photo"
                          helperText="Aadhaar / PAN / Voter ID"
                          testId="upload-govt-id-photo"
                        />
                        <ImageUploadField
                          label="Banner Photo"
                          value={memberForm.banner_photo}
                          onChange={(url) => setMemberForm({ ...memberForm, banner_photo: url })}
                          imageType="banner_photo"
                          helperText="Cover image (3:1 ratio)"
                          aspect="banner"
                          testId="upload-banner-photo"
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <Label>Description / Bio</Label>
                      <textarea className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" value={memberForm.bio} onChange={(e) => setMemberForm({...memberForm, bio: e.target.value})} placeholder={memberForm.role === 'doctor' ? 'Brief about the doctor...' : 'Brief about the nurse...'} data-testid="member-bio" />
                    </div>

                    {/* Fees */}
                    <div className="border-t pt-4 mt-2">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">Fees & Services</h4>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Consultation Fee (INR)</Label>
                          <Input type="number" value={memberForm.consultation_fee} onChange={(e) => setMemberForm({...memberForm, consultation_fee: e.target.value})} placeholder="e.g. 500" data-testid="member-consultation-fee" />
                        </div>
                        <div>
                          <Label>Home Visit Fee (INR)</Label>
                          <Input type="number" value={memberForm.home_visit_fee} onChange={(e) => setMemberForm({...memberForm, home_visit_fee: e.target.value})} placeholder="e.g. 800" data-testid="member-home-visit-fee" />
                        </div>
                      </div>
                      {/* Available Services */}
                      <div className="mt-3">
                        <Label className="mb-2 block">Available Services</Label>
                        <div className="flex flex-wrap gap-2">
                          {['clinic', 'home_visit'].map(svc => (
                            <button key={svc} type="button" onClick={() => {
                              const current = memberForm.services || [];
                              setMemberForm({...memberForm, services: current.includes(svc) ? current.filter(s => s !== svc) : [...current, svc]});
                            }} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${memberForm.services?.includes(svc) ? 'bg-purple-100 border-purple-400 text-purple-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                              {svc === 'clinic' ? 'Clinic Visit' : 'Home Visit'}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Available Days */}
                    <div>
                      <Label className="mb-2 block">Available Days</Label>
                      <div className="flex flex-wrap gap-2">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                          <button key={day} type="button" onClick={() => {
                            const current = memberForm.available_days || [];
                            setMemberForm({...memberForm, available_days: current.includes(day) ? current.filter(d => d !== day) : [...current, day]});
                          }} className={`w-10 h-10 rounded-lg text-xs font-semibold border transition-all ${memberForm.available_days?.includes(day) ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>

                    <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 text-white" disabled={addingMember} data-testid="submit-add-member">
                      {addingMember ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                      Add {memberForm.role === 'doctor' ? 'Doctor' : 'Nurse'} to Clinic
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Members List */}
            {members.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700">No Members Yet</h3>
                  <p className="text-slate-500 mt-2 max-w-md mx-auto">Start by adding doctors or nurses to your clinic. They'll be listed on Physign and patients can book appointments with them.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {members.map(member => (
                  <MemberDetailCard key={member.id} member={member} onToggle={handleToggleMemberAvailability} onRemove={handleRemoveMember} onEdit={handleStartEdit} editing={editingMember === member.id} editForm={editForm} setEditForm={setEditForm} onSaveEdit={handleSaveEdit} onCancelEdit={() => setEditingMember(null)} savingEdit={savingEdit} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Clinic Profile Tab */}
          <TabsContent value="profile">
            <Card data-testid="clinic-profile-form">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5 text-purple-500" />
                  Clinic Profile
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveClinicProfile} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Clinic Name *</Label>
                      <Input value={clinicForm.clinic_name} onChange={(e) => setClinicForm({...clinicForm, clinic_name: e.target.value})} placeholder="Sharma Health Clinic" data-testid="clinic-name-input" />
                    </div>
                    <div>
                      <Label>City / Location</Label>
                      <Input value={clinicForm.location} onChange={(e) => setClinicForm({...clinicForm, location: e.target.value})} placeholder="e.g. Delhi, Mumbai" data-testid="clinic-location-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Full Address</Label>
                    <Input value={clinicForm.clinic_address} onChange={(e) => setClinicForm({...clinicForm, clinic_address: e.target.value})} placeholder="123, MG Road, Sector 5, Delhi" data-testid="clinic-address-input" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <textarea
                      className="w-full min-h-[100px] px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      value={clinicForm.bio}
                      onChange={(e) => setClinicForm({...clinicForm, bio: e.target.value})}
                      placeholder="Tell patients about your clinic..."
                      data-testid="clinic-description-input"
                    />
                  </div>
                  <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white" disabled={savingProfile} data-testid="save-clinic-profile-btn">
                    {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Profile
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// Compact member card for overview
function MemberCard({ member, onToggle, onRemove }) {
  let bioParsed = {};
  try { bioParsed = JSON.parse(member.profile?.bio || '{}'); } catch {}
  const profilePhoto = toAbsoluteUrl(bioParsed.profile_photo || member.avatar);
  return (
    <div className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-white hover:shadow-sm transition-shadow" data-testid={`member-card-${member.id}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden ${member.role === 'doctor' ? 'bg-blue-100' : 'bg-rose-100'}`}>
          {profilePhoto ? (
            <img src={profilePhoto} alt={member.name} className="w-full h-full object-cover" />
          ) : (
            member.role === 'doctor' ? <Stethoscope className="w-5 h-5 text-blue-600" /> : <Heart className="w-5 h-5 text-rose-600" />
          )}
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-900">{member.name}</p>
          <p className="text-xs text-slate-500">{member.profile?.specialization || member.role} {member.profile?.qualification ? `| ${member.profile.qualification}` : ''}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge className={member.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'} data-testid={`member-status-${member.id}`}>
          {member.is_active ? 'Active' : 'Inactive'}
        </Badge>
        <span className="text-xs text-slate-500">{member.appointment_count || 0} bookings</span>
      </div>
    </div>
  );
}

// Detailed member card for members tab with edit support
function MemberDetailCard({ member, onToggle, onRemove, onEdit, editing, editForm, setEditForm, onSaveEdit, onCancelEdit, savingEdit }) {
  return (
    <Card className="border-l-4" style={{ borderLeftColor: member.role === 'doctor' ? '#3B82F6' : '#F43F5E' }} data-testid={`member-detail-${member.id}`}>
      <CardContent className="p-4">
        {editing ? (
          /* Edit Mode */
          <div className="space-y-3" data-testid={`edit-form-${member.id}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Edit className="w-4 h-4 text-purple-500" />Edit {member.role === 'doctor' ? 'Doctor' : 'Nurse'}
              </h3>
              <Button variant="ghost" size="sm" onClick={onCancelEdit}><X className="w-4 h-4" /></Button>
            </div>
            {/* Basic Info */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Name</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="edit-name" /></div>
              <div><Label className="text-xs">Email</Label><Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} data-testid="edit-email" /></div>
            </div>
            {/* Professional */}
            <div className="border-t pt-3 mt-1">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Professional Details</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Specialization</Label><Input value={editForm.specialization} onChange={e => setEditForm({...editForm, specialization: e.target.value})} data-testid="edit-specialization" /></div>
                <div><Label className="text-xs">Qualification</Label><Input value={editForm.qualification} onChange={e => setEditForm({...editForm, qualification: e.target.value})} data-testid="edit-qualification" /></div>
                <div><Label className="text-xs">Experience (yrs)</Label><Input type="number" value={editForm.experience_years} onChange={e => setEditForm({...editForm, experience_years: e.target.value})} data-testid="edit-experience" /></div>
                <div><Label className="text-xs">{member.role === 'doctor' ? 'Medical License No.' : 'Nursing Registration No.'}</Label><Input value={editForm.license_number} onChange={e => setEditForm({...editForm, license_number: e.target.value})} placeholder={member.role === 'doctor' ? 'e.g. MH/12345/2020' : 'e.g. NUR/12345'} data-testid="edit-license" /></div>
                <div><Label className="text-xs">Govt ID (Aadhaar/PAN)</Label><Input value={editForm.govt_id} onChange={e => setEditForm({...editForm, govt_id: e.target.value})} placeholder="e.g. 1234-5678-9012" data-testid="edit-govt-id" /></div>
                <div><Label className="text-xs">Clinic/Visit Address</Label><Input value={editForm.clinic_address} onChange={e => setEditForm({...editForm, clinic_address: e.target.value})} data-testid="edit-clinic-address" /></div>
              </div>
            </div>
            {/* Photo Uploads */}
            <div className="border-t pt-3 mt-1">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Documents & Photos</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`edit-photo-uploads-${member.id}`}>
                <ImageUploadField
                  label="Profile Photo"
                  value={editForm.profile_photo}
                  onChange={(url) => setEditForm({ ...editForm, profile_photo: url })}
                  imageType="profile_photo"
                  testId="edit-upload-profile-photo"
                />
                <ImageUploadField
                  label="Govt ID Photo"
                  value={editForm.govt_id_photo}
                  onChange={(url) => setEditForm({ ...editForm, govt_id_photo: url })}
                  imageType="govt_id_photo"
                  testId="edit-upload-govt-id-photo"
                />
                <ImageUploadField
                  label="Banner Photo"
                  value={editForm.banner_photo}
                  onChange={(url) => setEditForm({ ...editForm, banner_photo: url })}
                  imageType="banner_photo"
                  aspect="banner"
                  testId="edit-upload-banner-photo"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="text-xs">Description / Bio</Label>
              <textarea className="w-full min-h-[70px] px-3 py-2 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500" value={editForm.bio} onChange={e => setEditForm({...editForm, bio: e.target.value})} placeholder="About this professional..." data-testid="edit-bio" />
            </div>
            {/* Fees */}
            <div className="border-t pt-3 mt-1">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Fees & Services</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Consultation Fee (INR)</Label><Input type="number" value={editForm.consultation_fee} onChange={e => setEditForm({...editForm, consultation_fee: e.target.value})} data-testid="edit-consultation-fee" /></div>
                <div><Label className="text-xs">Home Visit Fee (INR)</Label><Input type="number" value={editForm.home_visit_fee} onChange={e => setEditForm({...editForm, home_visit_fee: e.target.value})} data-testid="edit-home-visit-fee" /></div>
              </div>
              <div className="mt-2">
                <Label className="text-xs mb-1 block">Available Services</Label>
                <div className="flex flex-wrap gap-2">
                  {['clinic', 'home_visit'].map(svc => (
                    <button key={svc} type="button" onClick={() => {
                      const current = editForm.services || [];
                      setEditForm({...editForm, services: current.includes(svc) ? current.filter(s => s !== svc) : [...current, svc]});
                    }} className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${editForm.services?.includes(svc) ? 'bg-purple-100 border-purple-400 text-purple-700' : 'bg-white border-slate-200 text-slate-500'}`}>
                      {svc === 'clinic' ? 'Clinic Visit' : 'Home Visit'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {/* Available Days */}
            <div>
              <Label className="text-xs mb-1 block">Available Days</Label>
              <div className="flex flex-wrap gap-1.5">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <button key={day} type="button" onClick={() => {
                    const current = editForm.available_days || [];
                    setEditForm({...editForm, available_days: current.includes(day) ? current.filter(d => d !== day) : [...current, day]});
                  }} className={`w-9 h-9 rounded-lg text-[11px] font-semibold border transition-all ${editForm.available_days?.includes(day) ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-slate-200 text-slate-500'}`}>
                    {day}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={onSaveEdit} disabled={savingEdit} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-edit-btn">
                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save Changes
              </Button>
              <Button variant="outline" onClick={onCancelEdit}>Cancel</Button>
            </div>
          </div>
        ) : (
          /* View Mode */
          (() => {
            let bioParsed = {};
            try { bioParsed = JSON.parse(member.profile?.bio || '{}'); } catch {}
            const profilePhoto = toAbsoluteUrl(bioParsed.profile_photo || member.avatar);
            const bannerPhoto = toAbsoluteUrl(bioParsed.banner_photo);
            const govtIdPhoto = toAbsoluteUrl(bioParsed.govt_id_photo);
            return (
            <>
              {bannerPhoto && (
                <div className="-m-4 mb-3 aspect-[5/1] overflow-hidden bg-slate-100 rounded-t-md">
                  <img src={bannerPhoto} alt="Banner" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 overflow-hidden ${member.role === 'doctor' ? 'bg-blue-100' : 'bg-rose-100'}`}>
                    {profilePhoto ? (
                      <img src={profilePhoto} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      member.role === 'doctor' ? <Stethoscope className="w-6 h-6 text-blue-600" /> : <Heart className="w-6 h-6 text-rose-600" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{member.name}</h3>
                    <p className="text-sm text-slate-500 capitalize">{member.role} {member.profile?.specialization ? `- ${member.profile.specialization}` : ''}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                      {member.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{member.phone}</span>}
                      {member.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{member.email}</span>}
                      {member.profile?.qualification && <span className="flex items-center gap-1"><Activity className="w-3 h-3" />{member.profile.qualification}</span>}
                      {member.profile?.experience_years && <span>{member.profile.experience_years} yrs exp</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      {member.profile?.consultation_fee && (
                        <span className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600 flex items-center gap-1">
                          <IndianRupee className="w-3 h-3" />{member.profile.consultation_fee} (Consult)
                        </span>
                      )}
                      {member.profile?.home_visit_fee && (
                        <span className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-600 flex items-center gap-1">
                          <IndianRupee className="w-3 h-3" />{member.profile.home_visit_fee} (Home Visit)
                        </span>
                      )}
                      {govtIdPhoto && (
                        <a href={govtIdPhoto} target="_blank" rel="noreferrer" className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded flex items-center gap-1 hover:bg-purple-100" data-testid={`view-govt-id-${member.id}`}>
                          <IdCard className="w-3 h-3" />Govt ID
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                  <div className="flex items-center gap-2">
                    <Badge className={member.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}>
                      {member.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {member.profile?.approval_status === 'approved' && (
                      <Badge className="bg-blue-100 text-blue-700">
                        <CheckCircle className="w-3 h-3 mr-1" />Verified
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <Button variant="outline" size="sm" onClick={() => onEdit(member)} className="text-purple-600 border-purple-200 hover:bg-purple-50" data-testid={`edit-member-${member.id}`}>
                      <Edit className="w-3.5 h-3.5 mr-1" />Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => onToggle(member.id)} data-testid={`toggle-member-${member.id}`}
                      className={member.is_active ? 'text-amber-600 border-amber-200 hover:bg-amber-50' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}>
                      <Power className="w-3.5 h-3.5 mr-1" />{member.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50" onClick={() => onRemove(member.id, member.name)} data-testid={`remove-member-${member.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" />Remove
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{member.appointment_count || 0} total bookings</p>
                </div>
              </div>
            </>
            );
          })()
        )}
      </CardContent>
    </Card>
  );
}


// Appointment card for clinic dashboard
function ClinicAppointmentCard({ apt, onAction }) {
  const statusStyles = {
    pending: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
    confirmed: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
    completed: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
    cancelled: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', badge: 'bg-red-100 text-red-600' },
    rejected: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-600', badge: 'bg-slate-100 text-slate-600' },
  };
  const style = statusStyles[apt.status] || statusStyles.pending;

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d; }
  };

  return (
    <Card className={`${style.bg} border ${style.border}`} data-testid={`clinic-apt-${apt.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0">
              <User className="w-5 h-5 text-slate-500" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">{apt.patient?.name || 'Patient'}</p>
              <p className="text-xs text-slate-500">{apt.patient?.phone}</p>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <Badge className={style.badge}>{apt.status}</Badge>
                <span className="text-xs text-slate-600 flex items-center gap-1">
                  <Stethoscope className="w-3 h-3" />{apt.provider?.name || 'Provider'}
                </span>
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />{formatDate(apt.appointment_date)}
                </span>
                {apt.time_slot && <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" />{apt.time_slot}</span>}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs bg-white px-2 py-0.5 rounded border border-slate-200 capitalize flex items-center gap-1">
                  {apt.service_type === 'home_visit' ? <Home className="w-3 h-3" /> : <Building className="w-3 h-3" />}
                  {apt.service_type?.replace('_', ' ') || 'Visit'}
                </span>
                {apt.fee && <span className="text-xs font-semibold text-slate-700 flex items-center gap-0.5"><IndianRupee className="w-3 h-3" />{apt.fee}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {apt.status === 'pending' && (
              <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onAction(apt.id, 'confirmed')} data-testid={`confirm-apt-${apt.id}`}>
                  <CheckCircle className="w-3.5 h-3.5 mr-1" />Confirm
                </Button>
                <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50" onClick={() => onAction(apt.id, 'rejected')} data-testid={`reject-apt-${apt.id}`}>
                  <X className="w-3.5 h-3.5 mr-1" />Reject
                </Button>
              </>
            )}
            {apt.status === 'confirmed' && (
              <>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => onAction(apt.id, 'completed')} data-testid={`complete-apt-${apt.id}`}>
                  <CheckCircle className="w-3.5 h-3.5 mr-1" />Complete
                </Button>
                <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50" onClick={() => onAction(apt.id, 'cancelled')} data-testid={`cancel-apt-${apt.id}`}>
                  <X className="w-3.5 h-3.5 mr-1" />Cancel
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
